import { Reveal } from '@/components/dashboard/fx/Reveal'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'

const ARTICLE_URL =
  'https://medium.com/@drdavidwbell/how-peloton-apple-and-spotify-engineer-their-workout-playlists-and-why-most-of-them-get-it-cf754f0b53b5'

// A tongue-in-cheek "tale of the tape". Everything on the Crescendo side is
// as reported by the linked article -- attributed, not asserted.
const TAPE: { label: string; them: string; us: React.ReactNode }[] = [
  { label: 'Built by', them: 'A startup Peloton bought in 2018', us: 'One guy after work' },
  { label: 'Song library', them: '1,000,000+ licensed tracks', us: 'Whatever has already shown up in a class' },
  { label: 'Searches by', them: 'BPM and duration', us: <>&ldquo;Wait, didn&apos;t they just play this?&rdquo;</> },
  { label: 'The team', them: 'A Head of Music and five music supervisors', us: 'Me and a Peloton bike' },
  { label: "Knows a song's tempo", them: 'Yes', us: 'Absolutely not' },
  { label: 'Has opinions about punk', them: 'Unclear', us: 'Unfortunately, many' },
]

function ArticleLink({ children }: { children: React.ReactNode }) {
  return (
    <a href={ARTICLE_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-foreground underline decoration-primary/50 underline-offset-4 transition-colors hover:decoration-primary">
      {children}
    </a>
  )
}

export function CrescendoNote() {
  return (
    <Reveal>
      <SpotlightCard className="p-6 sm:p-8">
        <div className="mb-6 flex flex-col gap-2">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Plot twist</span>
          <h2 className="font-display text-3xl font-semibold tracking-tighter sm:text-4xl">
            Wait, Peloton already built this?
          </h2>
        </div>

        {/* The setup, beside the comparison it leads into. */}
        <div className="grid items-start gap-x-14 gap-y-8 lg:grid-cols-2">
          <div className="flex flex-col gap-4 text-base leading-relaxed sm:text-[1.0625rem]">
            <p>Well... sort of.</p>
            <p>
              While digging into how Peloton actually picks music for classes, I found <ArticleLink>this article</ArticleLink>, which sent me down a pretty great rabbit hole.
            </p>
            <p>
              Apparently Peloton has a Head of Music, five music supervisors who work with specific instructors, and an internal tool called <strong>Crescendo</strong> that lets instructors search more
              than a million licensed songs by things like BPM and duration.
            </p>
            <p>They reportedly bought a startup in 2018 just to build it.</p>
            <p>I have a scraper and an unreasonable amount of curiosity about why certain songs keep showing up.</p>
          </div>

          <table className="w-full border-collapse text-sm">
            <caption className="mb-3 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">The tale of the tape</caption>
            <thead>
              <tr className="text-left">
                <th scope="col" className="sr-only">
                  Category
                </th>
                <th scope="col" className="pb-3 pr-4 font-display text-base font-semibold">
                  Crescendo
                </th>
                <th scope="col" className="rounded-t-xl bg-primary/[0.07] px-4 pb-3 pt-3 font-display text-base font-semibold text-primary">
                  This site
                </th>
              </tr>
            </thead>
            <tbody>
              {TAPE.map((row, i) => (
                <tr key={row.label} className="border-t border-border/60 align-top">
                  <th scope="row" className="py-2.5 pr-4 text-left text-xs font-normal text-muted-foreground lg:w-40 lg:whitespace-nowrap">
                    {row.label}
                  </th>
                  <td className="py-2.5 pr-4">{row.them}</td>
                  <td className={`bg-primary/[0.07] px-4 py-2.5 font-medium ${i === TAPE.length - 1 ? 'rounded-b-xl' : ''}`}>{row.us}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* The verdict: the answer, then why it isn't really a contest. Grouped explicitly (not
            flowed through CSS columns, which split "So we each have our strengths." off on its
            own at the top of the second column). */}
        <div className="mt-8 grid items-start gap-x-14 gap-y-4 text-base leading-relaxed sm:text-[1.0625rem] lg:grid-cols-2">
          <div className="flex flex-col gap-4">
            <p className="font-display text-2xl font-semibold tracking-tight">So is Crescendo better than this site?</p>
            <p>Almost definitely. They have a million songs and an actual music department.</p>
            <p>
              But I&apos;m pretty confident Crescendo does <strong>not</strong> have a dashboard whose primary purpose is discovering that an instructor has played the same song 47 times.
            </p>
            <p>So we each have our strengths.</p>
          </div>
          <div className="flex flex-col gap-4 lg:pt-[2.6rem]">
            <p>Really, the two tools are answering completely different questions. Crescendo helps instructors find music that fits the ride they&apos;re building.</p>
            <p>This site looks backward and asks: what are instructors actually playing, what do they keep coming back to, and what somehow never gets played at all?</p>
          </div>
        </div>

        <figure className="my-6 border-l-2 border-primary/60 py-1 pl-6 sm:pl-8">
          <blockquote className="max-w-4xl font-accent text-3xl italic leading-tight sm:text-4xl">
            &ldquo;They choose their own. We don&apos;t promote anything, musically speaking.&rdquo;
          </blockquote>
          <figcaption className="mt-4 text-sm text-muted-foreground">
            Paul DeGooyer, Peloton&apos;s former Head of Music, <span className="opacity-80">as quoted in the article</span>
          </figcaption>
        </figure>

        <div className="gap-x-14 text-base leading-relaxed sm:text-[1.0625rem] lg:columns-2 [&>*]:break-inside-avoid [&>*+*]:mt-4">
          <p>And honestly, that might be my favorite part.</p>
          <p>
            The music supervisors are there to help, but instructors still choose their own music. So when the same songs keep popping up, it&apos;s probably not some mysterious Peloton algorithm stuck in
            a loop.
          </p>
          <p>It&apos;s just people having favorites.</p>
          <p>Which makes this whole thing way more interesting.</p>
          <p>
            At its core, this site is really just a very elaborate way of looking at instructor taste at scale. And, every once in a while, wondering whether that taste could use just a tiny bit more
            punk.
          </p>
          <p className="text-base text-muted-foreground">
            One other fun detail from the article: it cites a Goldman Sachs estimate that Peloton pays around <strong className="font-semibold text-foreground">3.1 cents per stream</strong>, compared with
            roughly <strong className="font-semibold text-foreground">0.35 cents on Spotify</strong>. So the next time you hear the same song for the hundredth time, at least somebody is getting paid.{' '}
            <ArticleLink>Read the full article</ArticleLink>.
          </p>
        </div>
      </SpotlightCard>
    </Reveal>
  )
}
